import { Fragment, useRef, useState, useEffect } from "react";

//Vapor Components
import Stack from "@vapor/react-material/Stack";
import Box from "@vapor/react-material/Box";
import Button from "@vapor/react-material/Button";
import Modal from "@vapor/react-material/Modal";
import Typography from "@vapor/react-extended/ExtendedTypography";
import Divider from "@vapor/react-material/Divider";
import Tabs from "@vapor/react-material/Tabs";
import Tab from "@vapor/react-material/Tab";
import CircularProgress from "@vapor/react-material/CircularProgress";
import Snackbar from "@vapor/react-material/Snackbar";
import Alert from "@vapor/react-material/Alert";

//Icons
import { PictureAsPdf as PdfIcon } from "@mui/icons-material";

//Style
import { modalStyleUltraLarge } from "../../businessLogic/styles";

// Import dati di esempio
import { StatsData } from "./StatsData";
import sharedDatasInstance from "../../businessLogic/sharedDatas";

// Import componenti TabPanel
import ClientiStatsTab from "./ClientiStatsTab";
import PrestazioniStatsTab from "./PrestazioniStatsTab";
import AutovalutazioniStatsTab from "./AutovalutazioniStatsTab";

// Import nuovi componenti e hook
import { RISK_COLORS, GENERAL_COLORS } from "./StatComponents";
import { usePdfExport } from "./usePdfExport";
import { Loading } from "../Loading";
import { LogApi } from "../../businessLogic/api/logApi";

export const StatsModal = ({ show, onClose }) => {
    const contentRef = useRef();
    const statsData = StatsData();
    const [currentStats, setCurrentStats] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isExportingPdf, setIsExportingPdf] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
    const tenantModel = sharedDatasInstance.getPropertyByName("tenantModel");
    const tenantTypeId = sharedDatasInstance.getPropertyByName("userModel")?.tenantTipoId ?? null;
    const aziendaName = sharedDatasInstance.getPropertyByName("aziendaName");
    const logApi = LogApi();
    const { exportToPdf } = usePdfExport();
    const currentTenantInfo = {
        ragioneSociale: tenantModel?.name ?? "Non indicata",
        azienda : aziendaName ?? "Non indicata",
        partitaIva: tenantModel?.vatNumber ?? "Non indicata",       
        sede: tenantModel?.addrFull ?? "Non indicato"
    };


    useEffect(() => {
        if (show) {
            loadStatsData();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);
    
    const loadStatsData = async () => {
        setIsLoading(true);
        try {
            // Prova prima a recuperare dati reali
            const realData = await statsData.getRealStatsData();
            setCurrentStats(realData);
        } catch (error) {
            console.error('Errore nel caricamento dati, uso dati di esempio:', error);
            logApi.AddLog(error.message,error.stack,"loadStatsData","StatsModal");
        } finally {
            setIsLoading(false);
        }
    };    
    const displayStats = currentStats;
    
    const handleModalClose = (e) => {
        onClose(e);
    };
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const TabPanel = ({ children, value, index, ...other }) => {
        return (
            <div
                role="tabpanel"
                hidden={value !== index}
                id={`stats-tabpanel-${index}`}
                aria-labelledby={`stats-tab-${index}`}
                {...other}
            >
                {value === index && (
                    <Box sx={{ pt: 3 }}>
                        {children}
                    </Box>
                )}
            </div>
        );
    };

    const handleExportPDF = async () => {
        setIsExportingPdf(true);
        try {
            await exportToPdf(contentRef, activeTab, currentTenantInfo);
            setNotification({
                open: true,
                message: 'PDF esportato con successo!',
                severity: 'success'
            });
        } catch (error) {
            console.error('Errore durante l\'esportazione PDF:', error);
            setNotification({
                open: true,
                message: 'Errore durante l\'esportazione del PDF. Riprova.',
                severity: 'error'
            });
        } finally {
            setIsExportingPdf(false);
        }
    };

    const handleCloseNotification = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setNotification(prev => ({ ...prev, open: false }));
    };

    return (
        <Fragment>
            <Modal
                key="statsModal"
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
                open={show}
                onClose={handleModalClose}
            >                
                <Box sx={modalStyleUltraLarge}>
                    {isExportingPdf && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 1000,
                                backdropFilter: 'blur(2px)'
                            }}
                        >
                            <Loading />
                            <Typography variant="h6" sx={{ mt: 2, color: '#1976d2' }}>
                                Generazione PDF in corso...
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1, color: '#666' }}>
                                Attendi qualche istante
                            </Typography>
                        </Box>
                    )}
                    <Box sx={{ maxHeight: '80vh', overflow: 'auto' }}>
                        {isExportingPdf && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    zIndex: 1000,
                                    backdropFilter: 'blur(3px)'
                                }}
                            >
                                <Loading />
                                <Typography variant="h6" sx={{ mt: 2, color: '#1976d2' }}>
                                    Generazione PDF in corso...
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 1, color: '#666' }}>
                                    Attendi qualche istante
                                </Typography>
                            </Box>
                        )}
                        <Box ref={contentRef} sx={{ p: 3 }}>
                            <Typography variant="h4" sx={{ mb: 3, textAlign: 'center', color: '#000000ff', fontSize: '1.5rem' }}>
                                Statistiche di Autovalutazione                                
                                <Typography variant="body1" sx={{ textAlign: 'center', color: '#000000ff' }}>
                                    Azienda: {tenantModel.name} - Soggetto Obbligato : {aziendaName}
                                </Typography>
                            </Typography>
                            
                            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                                <Tabs 
                                    value={activeTab} 
                                    onChange={handleTabChange} 
                                    aria-label="statistiche tabs"
                                    centered
                                >
                                    <Tab label="Statistiche Clienti" id="stats-tab-0" aria-controls="stats-tabpanel-0" />
                                    <Tab label="Statistiche Prestazioni" id="stats-tab-1" aria-controls="stats-tabpanel-1" />
                                    {tenantTypeId === 2 &&
                                        <Tab label="Statistiche Autovalutazioni" id="stats-tab-2" aria-controls="stats-tabpanel-2" />
                                    }
                                </Tabs>
                            </Box>
                            
                            <TabPanel value={activeTab} index={0}>
                                <ClientiStatsTab 
                                    isLoading={isLoading}
                                    displayStats={displayStats}
                                    riskColors={RISK_COLORS}
                                    generalColors={GENERAL_COLORS}
                                />
                            </TabPanel>
                            
                            <TabPanel value={activeTab} index={1}>
                                <PrestazioniStatsTab 
                                    isLoading={isLoading}
                                    displayStats={displayStats}
                                    riskColors={RISK_COLORS}
                                />
                            </TabPanel>

                            {tenantTypeId === 2 &&
                                <TabPanel value={activeTab} index={2}>
                                    <AutovalutazioniStatsTab 
                                        isLoading={isLoading}
                                        displayStats={displayStats}
                                        riskColors={RISK_COLORS}
                                    />
                                </TabPanel>
                            }
                        </Box>
                    </Box>
                    
                    <Divider />
                    
                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{ p: 2, bgcolor: "#F2F5F8" }}
                        justifyContent="flex-end"
                        alignItems="center"
                    >
                        <Button 
                            variant="contained" 
                            size="small" 
                            startIcon={isExportingPdf ? <CircularProgress size={16} color="inherit" /> : <PdfIcon />}
                            onClick={handleExportPDF}
                            disabled={isExportingPdf || isLoading}
                            sx={{ 
                                backgroundColor: '#d32f2f', 
                                '&:hover': { backgroundColor: '#b71c1c' },
                                '&:disabled': { backgroundColor: '#ffcdd2', color: '#d32f2f' }
                            }}
                        >
                            {isExportingPdf ? 'Generazione...' : 'Esporta PDF'}
                        </Button>
                        <Button 
                            variant="outlined" 
                            size="small" 
                            onClick={handleModalClose}
                            disabled={isExportingPdf}
                        >
                            Chiudi
                        </Button>
                    </Stack>
                </Box>
            </Modal>
            
            <Snackbar 
                open={notification.open} 
                autoHideDuration={4000} 
                onClose={handleCloseNotification}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert 
                    onClose={handleCloseNotification} 
                    severity={notification.severity} 
                    sx={{ width: '100%' }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </Fragment>
    );
};